<!--Copyright (C) nexleader - All Rights Reserved-->
<!--Unauthorized copying of this file, via any medium is strictly prohibited-->
<!--Proprietary and confidential-->
<!--Written for nexleader <myipsat.com>, 2016-2026-->

<!-- Loading -->
<div class="hideOnPrint text-center p-xl" *ngIf="isLoading">
  <i class="fa fa-spinner fa-spin fa-3x"></i>
  <p class="m-t-md">Loading report…</p>
</div>

<!-- No report fallback -->
<div class="hideOnPrint text-center p-xl" *ngIf="!isLoading && !archetypeReport">
  <i class="fa fa-exclamation-circle fa-3x text-warning m-b-md"></i>
  <h4>No IPSAT 2.0 Report Found</h4>
</div>

<!-- Print / download buttons (visible on screen, hidden when printing) -->
<div class="hideOnPrint text-center m-b-md" *ngIf="!isLoading && archetypeReport">
  <button class="btn btn-success btn-lg" (click)="print()">
    <i class="fa fa-print"></i>&nbsp;&nbsp;Print or Save as PDF
  </button>
  <!-- Server-rendered PDF ("Page N of M" page numbers, which Chrome can't
       produce from browser print on this free-flow report).

       HIDDEN 2026-09-13 at Steve's request: the route 500s on production
       because it drives headless Chrome via puppeteer-core and there is no
       Chrome/Chromium binary on the box (config.puppeteerExecutablePath is
       unset). Installing it needs ~160 packages through an apt that has been
       wedged for a year, which we were not willing to do on launch weekend.

       Everything behind this button still works: downloadPdf() and
       ArchetypeReportService.getPdf() are untouched. To restore: install
       Chrome, set config.puppeteerExecutablePath, un-comment this block.
       Until then "Print or Save as PDF" above is the working path; it just
       has no page numbers.
  <button class="btn btn-primary btn-lg m-l-sm" (click)="downloadPdf()" [disabled]="isDownloadingPdf">
    <i class="fa" [ngClass]="isDownloadingPdf ? 'fa-spinner fa-spin' : 'fa-file-pdf-o'"></i>&nbsp;&nbsp;{{ isDownloadingPdf ? 'Preparing PDF…' : 'Download PDF (numbered)' }}
  </button>
  -->
</div>

<!-- Printable content -->
<div class="printable-report" *ngIf="!isLoading && archetypeReport">

  <!-- ============================================================ -->
  <!-- Section 1 — Cover                                            -->
  <!-- ============================================================ -->
  <section class="cover">
    <div class="cover-icon" aria-label="IPSAT" role="img">
      <!-- Inlined Ipsat Icon.svg, fill set directly to brand IPSAT green so -->
      <!-- the CSS override in printable-archetype-report.component.scss is  -->
      <!-- no longer load-bearing. (Was #4CBA72 — Steve flagged that shade   -->
      <!-- as "pastel" 2026-06-04.)                                          -->
      <svg viewBox="0 0 263 209" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <g fill="#44b25d" fill-rule="evenodd">
          <path d="M66.9562385,0 L0,66.692631 L131.594145,208.31562 L263,66.511401 L196.57333,0 L66.9562385,0 Z M79.8047377,28.2083281 L183.385907,28.2083281 L209.426536,53.789998 L53.7570475,53.789998 L79.8047377,28.2083281 Z M152.249655,81.998327 L209.033479,81.998327 L180.641567,112.873324 L152.249655,81.998327 Z M54.1524583,81.998327 L110.929221,81.998327 L82.5326019,112.866263 L54.1524583,81.998327 Z M101.881844,133.910123 L131.589438,101.615883 L161.292325,133.919538 L131.594145,166.220839 L101.881844,133.910123 Z"/>
        </g>
      </svg>
    </div>
    <div class="cover-eyebrow">Identity Profile Self-Awareness Tool</div>
    <h1 class="cover-title">IPSAT 2.0</h1>
    <div class="cover-tagline">
      <em>Discover your Identity.<br />Reveal your Archetype.</em>
    </div>
    <div class="cover-meta">
      <p><strong>Prepared for:</strong> {{ fullName }}</p>
      <p><strong>Completed:</strong> {{ getFormattedDate() }}</p>
    </div>
  </section>

  <!-- ============================================================ -->
  <!-- Section 2 — Introduction                                     -->
  <!-- ============================================================ -->
  <section class="report-section">
    <h2>Introduction</h2>
    <p>The IPSAT integrates two streams of insight:</p>
    <ul>
      <li>
        <strong>Five self-discovery tools</strong> that reflect your Identity Profile —
        personality, strengths, skills, {{ categoryHeading.toLowerCase() }}, and passions.
      </li>
      <li>
        <strong>Five self-authored statements</strong> that describe how you show up in life —
        Identity Overview, Best Contribution, Developmental Priority, Potential Derailer,
        and Vulnerable Setting.
      </li>
    </ul>
    <p>
      Together, these ten data points create your IPSAT Archetype, revealing the recurring
      pattern of how you think, lead, relate, and grow. The combination of assessment results
      and your narrative voice allows your story to help interpret the data rather than the
      data defining your Identity.
    </p>

    <h3 class="m-t-lg">How the Six Archetypes Were Identified</h3>
    <p>
      The six IPSAT Archetypes were not created — they were discovered. When we analyzed the
      full IPSAT dataset, clear and consistent patterns emerged across personality types,
      strengths, gifts, passions, and self-authored identity statements. These natural clusters
      revealed six distinct ways people tend to operate at their best. The archetypes simply
      give language to those data-driven patterns.
    </p>

    <div class="intro-wheel">
      <h4 class="text-center m-b-md">The Six IPSAT Archetypes</h4>
      <app-archetype-wheel primary="" secondary=""></app-archetype-wheel>
    </div>
  </section>

  <!-- ============================================================ -->
  <!-- Section 3 — Integrating Your Identity Profile and Archetype  -->
  <!--             Framework + Overview + Dynamic Pattern Table     -->
  <!-- ============================================================ -->
  <section class="report-section">
    <h2>Integrating Your Identity Profile and Archetype Framework</h2>

    <table class="kv-table m-b-md">
      <tbody>
        <tr>
          <th>Primary Archetype</th>
          <td>{{ primaryArchetypeName }}</td>
        </tr>
        <tr *ngIf="overlayArchetypeName">
          <th>Overlay Archetype</th>
          <td>{{ overlayArchetypeName }}</td>
        </tr>
        <!-- Confidence row removed 2026-06-08 per Steve: "very confusing -->
        <!-- even for coaches" — must be removed from report as well as   -->
        <!-- the dashboard, not just one or the other.                    -->
        <tr *ngIf="archetypeReport.ipsatScore !== undefined">
          <th>Overall IPSAT Score</th>
          <td>{{ archetypeReport.ipsatScore }} / 100</td>
        </tr>
        <tr *ngIf="archetypeReport.stage?.name">
          <th>Stage of Identity Self-Awareness</th>
          <td>{{ archetypeReport.stage?.name }}</td>
        </tr>
      </tbody>
    </table>

    <p *ngIf="archetypeReport.stage?.body" class="text-muted">{{ archetypeReport.stage?.body }}</p>

    <h3 class="m-t-lg">Your Archetype Overview</h3>
    <p>
      You are {{ ['A','E','I','O','U'].includes(primaryArchetypeName.charAt(0)) ? 'an' : 'a' }}
      <strong>{{ primaryArchetypeName }}</strong> — {{ primaryContent?.report_overview }}
    </p>
    <p *ngIf="overlayArchetypeName">
      Your <strong>{{ overlayArchetypeName }}</strong> overlay adds nuance to your primary
      pattern.
      <ng-container *ngIf="aiContent?.overlay_synthesis">{{ aiContent.overlay_synthesis }}</ng-container>
      <ng-container *ngIf="!aiContent?.overlay_synthesis">{{ overlayContent?.report_overview }}</ng-container>
    </p>
    <p *ngIf="atYourBestLine">
      <strong>When you're at your best:</strong> {{ atYourBestLine }}
    </p>
    <p *ngIf="greatestTemptationLine">
      <strong>Your greatest temptation:</strong> {{ greatestTemptationLine }}
    </p>

    <h3 class="m-t-lg">Dynamic Pattern for You</h3>
    <table class="table table-bordered dynamic-pattern-table">
      <thead>
        <tr>
          <th style="width: 30%;">Dynamic</th>
          <th>Pattern for You</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of dynamicPatternRows">
          <td><strong>{{ row.label }}</strong></td>
          <td>{{ row.text }}</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- ============================================================ -->
  <!-- Section 4 — Wheel + The Six IPSAT Archetypes                 -->
  <!-- ============================================================ -->
  <section class="report-section">
    <h3 class="text-center font-italic">Your Archetype is a lens, not a label.</h3>
    <div class="wheel-container">
      <app-archetype-wheel
        [primary]="primaryArchetypeName"
        [secondary]="overlayArchetypeName">
      </app-archetype-wheel>
    </div>
    <h4 class="text-center font-italic">It brings clarity, not conformity.</h4>

    <p class="m-t-lg">
      An Archetype Overlay identifies the secondary pattern that most shapes or amplifies
      your primary archetype. It adds dimension to your profile by revealing how another
      archetype's strengths and tendencies influence your overall style, creating a more
      complete picture of how you show up and lead.
    </p>

    <div class="m-t-lg">
      <div *ngFor="let arch of frameworkArchetypes" class="framework-archetype m-b-md">
        <div class="framework-row">
          <app-archetype-icon [archetype]="$any(arch.key)" [size]="32"></app-archetype-icon>
          <div class="framework-text">
            <h5 class="font-bold m-b-xs">
              {{ arch.typeNumber }}. {{ arch.name }}
              <span *ngIf="arch.key === toArchetypeKey(primaryArchetypeName)" class="label label-success">Your Primary</span>
              <span *ngIf="arch.key === toArchetypeKey(overlayArchetypeName)" class="label label-info">Your Overlay</span>
            </h5>
            <p class="m-b-none">{{ arch.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ============================================================ -->
  <!-- Section 5 — Why This Framework Is Unique + Identity Profile  -->
  <!--             Summary Table                                    -->
  <!-- ============================================================ -->
  <section class="report-section">
    <h2>Why This Framework Is Unique</h2>
    <p>
      The IPSAT Archetype Framework integrates five self-discovery assessments and five
      self-authored identity statements into a single pattern of contribution. Each
      archetype is distinct yet adjacent, illustrating how diverse strengths complement
      one another in teams and organizations. Your Primary and Overlay Archetypes show
      where you naturally create value and how you collaborate most effectively with others.
    </p>

    <h3 class="m-t-lg">Your Identity Profile Summary</h3>
    <table class="table table-bordered identity-summary-table">
      <thead>
        <tr>
          <th style="width: 20%;">Category</th>
          <th style="width: 35%;">Top Results</th>
          <th>Key Insight</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Personality</strong></td>
          <td>{{ topMbti }}</td>
          <td>{{ aiContent?.category_key_insights?.personality }}</td>
        </tr>
        <tr>
          <td><strong>Strengths</strong></td>
          <td>
            <ul class="compact-list">
              <li *ngFor="let s of topStrengths">{{ s }}</li>
            </ul>
          </td>
          <td>{{ aiContent?.category_key_insights?.strengths }}</td>
        </tr>
        <tr>
          <td><strong>Skills</strong></td>
          <td>
            <ul class="compact-list">
              <li *ngFor="let s of topSkills">{{ s }}</li>
            </ul>
          </td>
          <td>{{ aiContent?.category_key_insights?.skills }}</td>
        </tr>
        <tr>
          <td><strong>{{ categoryHeading }}</strong></td>
          <td>
            <ul class="compact-list">
              <li *ngFor="let s of topGiftsOrValues">{{ s }}</li>
            </ul>
          </td>
          <td>{{ aiContent?.category_key_insights?.gifts_or_values }}</td>
        </tr>
        <tr>
          <td><strong>Passions</strong></td>
          <td>
            <ul class="compact-list">
              <li *ngFor="let s of topPassions">{{ s }}</li>
            </ul>
          </td>
          <td>{{ aiContent?.category_key_insights?.passions }}</td>
        </tr>
      </tbody>
    </table>

    <p class="m-t-md" *ngIf="archetypeReport.ipsatScore !== undefined && archetypeReport.stage?.name">
      <strong>Overall IPSAT Score:</strong> {{ archetypeReport.ipsatScore }} / 100
      &nbsp;&nbsp;<strong>Stage:</strong> {{ archetypeReport.stage?.name }}
    </p>
  </section>

  <!-- ============================================================ -->
  <!-- Section 6 — Your Identity Statements (with Archetype Lens)   -->
  <!-- ============================================================ -->
  <section class="report-section" *ngIf="aiContent?.identity_statements?.length">
    <h2>Your Identity Statements (with Archetype Lens)</h2>
    <div *ngFor="let s of aiContent.identity_statements" class="m-b-md">
      <h4 class="font-bold m-b-xs">{{ s.statement_type }}</h4>
      <p class="font-italic m-b-xs">&ldquo;{{ s.statement_text }}&rdquo;</p>
      <p class="m-b-none" *ngIf="s.archetype_lens">
        <strong>Archetype Lens:</strong> {{ s.archetype_lens }}
      </p>
    </div>
  </section>

  <!-- ============================================================ -->
  <!-- Section 7 — Integrated Insights                              -->
  <!-- ============================================================ -->
  <section class="report-section" *ngIf="aiContent?.integrated_insights?.length">
    <h2>Integrated Insights</h2>
    <ul class="integrated-insights-list">
      <li *ngFor="let i of aiContent.integrated_insights" class="m-b-sm">
        <strong>{{ i.title }}:</strong> {{ i.body }}
      </li>
    </ul>
  </section>

  <!-- ============================================================ -->
  <!-- Section 8 — Counterintuitive Insights: REMOVED 2026-08-15    -->
  <!-- ============================================================ -->
  <!-- Steve 2026-08-15 ask ("important report revision"): pulled from   -->
  <!-- the report for 2.0 go-live. The counterintuitive_insights content -->
  <!-- is boilerplate for 5 of the 6 archetypes — identical text with    -->
  <!-- only the archetype name swapped. It arrived that way in Steve's   -->
  <!-- handoff library; only visionary_strategist.json was written for   -->
  <!-- real. Not a prompt issue: this section is static library content, -->
  <!-- never AI-generated. No time to write and user-test replacements   -->
  <!-- before launch. Section numbering below is left unchanged so the   -->
  <!-- slot stays reserved if Steve writes real content post-launch;     -->
  <!-- the JSON is untouched and still covered by ArchetypeContent.spec. -->

  <!-- ============================================================ -->
  <!-- Section 9 — 90-Day Development Focus                         -->
  <!-- ============================================================ -->
  <section class="report-section">
    <h2>Reflect &amp; Apply</h2>
    <p>
      Growth begins with awareness and matures through reflection, conversation, and
      intentional practice. Use these guiding questions over the next 90 days to clarify
      how you'll sustain momentum, strengthen growth edges, and serve others through your
      unique design.
    </p>

    <table class="table table-bordered development-focus-table">
      <thead>
        <tr>
          <th style="width: 22%;">Focus Area</th>
          <th>Guiding Questions</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Sustain</strong></td>
          <td>
            <ng-container *ngIf="aiContent?.development_focus?.sustain?.length; else sustainStatic">
              <p *ngFor="let q of aiContent.development_focus.sustain" class="m-b-xs">{{ q }}</p>
            </ng-container>
            <ng-template #sustainStatic>{{ primaryContent?.development_focus?.sustain }}</ng-template>
          </td>
        </tr>
        <tr>
          <td><strong>Strengthen</strong></td>
          <td>
            <ng-container *ngIf="aiContent?.development_focus?.strengthen?.length; else strengthenStatic">
              <p *ngFor="let q of aiContent.development_focus.strengthen" class="m-b-xs">{{ q }}</p>
            </ng-container>
            <ng-template #strengthenStatic>{{ primaryContent?.development_focus?.strengthen }}</ng-template>
          </td>
        </tr>
        <tr>
          <td><strong>Serve</strong></td>
          <td>
            <ng-container *ngIf="aiContent?.development_focus?.serve?.length; else serveStatic">
              <p *ngFor="let q of aiContent.development_focus.serve" class="m-b-xs">{{ q }}</p>
            </ng-container>
            <ng-template #serveStatic>{{ primaryContent?.development_focus?.serve }}</ng-template>
          </td>
        </tr>
      </tbody>
    </table>

    <p class="font-italic text-muted m-t-md">
      Capture one insight each week. After 90 days, review patterns of growth and resistance.
    </p>
  </section>

  <!-- ============================================================ -->
  <!-- Section 10 — Disclosure & Feedback Loop                      -->
  <!-- ============================================================ -->
  <section class="report-section" *ngIf="primaryContent?.disclosure_prompts">
    <h2>Disclosure &amp; Feedback Loop</h2>
    <p>
      Growth accelerates when awareness is shared. These disclosure statements are designed
      for honest dialogue with people in your inner and outer circles. Each one begins with
      <em>&ldquo;Something I'm learning about myself…&rdquo;</em> and ends with a practical
      feedback request. Start with high-trust relationships and work outward.
    </p>

    <ol class="disclosure-list m-t-md">
      <li *ngFor="let aud of disclosureAudiences" class="m-b-md">
        <h5 class="font-bold m-b-xs">
          {{ aud.label }} <span class="text-muted">— {{ aud.vulnerability }}</span>
        </h5>
        <p class="m-b-none" *ngIf="primaryContent?.disclosure_prompts?.[aud.key]">
          {{ primaryContent.disclosure_prompts[aud.key] }}
        </p>
      </li>
    </ol>
  </section>

  <!-- ============================================================ -->
  <!-- Section 11 — How [Primary] Interfaces with the Other Five    -->
  <!-- ============================================================ -->
  <section class="report-section" *ngIf="interactionRows.length">
    <h2>How the {{ primaryArchetypeName }} Interfaces with the Other Five</h2>
    <p>
      Understanding how your {{ primaryArchetypeName }} pattern interacts with the other
      five archetypes helps you balance empathy with execution and creativity with consistency.
    </p>

    <div class="m-t-lg">
      <div *ngFor="let row of interactionRows; let i = index" class="interaction-row m-b-md">
        <div class="interaction-header">
          <app-archetype-icon [archetype]="$any(row.archetypeKey)" [size]="28"></app-archetype-icon>
          <h5 class="font-bold m-b-xs">
            {{ i + 1 }}. With {{ row.archetypeName }}s<span *ngIf="row.rich?.title"> &mdash; {{ row.rich?.title }}</span>
          </h5>
        </div>
        <ng-container *ngIf="row.rich; else legacyRow">
          <p class="m-b-sm" *ngIf="row.rich.dynamic">{{ row.rich.dynamic }}</p>
          <p class="m-b-xs" *ngIf="row.rich.best_practices">
            <strong>Best Practices:</strong> {{ row.rich.best_practices }}
          </p>
          <p class="m-b-xs font-italic" *ngIf="row.rich.example">
            <strong>Example:</strong> &ldquo;{{ row.rich.example }}&rdquo;
          </p>
          <p class="m-b-none" *ngIf="row.rich.for_user">
            <strong>For {{ firstName || 'You' }}:</strong> {{ row.rich.for_user }}
          </p>
        </ng-container>
        <ng-template #legacyRow>
          <p class="m-b-none">{{ row.description }}</p>
        </ng-template>
      </div>
    </div>
  </section>

  <!-- ============================================================ -->
  <!-- Section 12 — Summary                                         -->
  <!-- ============================================================ -->
  <section class="report-section" *ngIf="aiContent?.final_summary">
    <h2>Summary</h2>
    <p>{{ aiContent.final_summary }}</p>
  </section>

  <!-- ============================================================ -->
  <!-- Section 13 — Appendix A — Original Identity Statements       -->
  <!-- ============================================================ -->
  <section class="report-section" *ngIf="aiContent?.identity_statements?.length">
    <h2>Appendix A &mdash; Your Original Identity Statements</h2>
    <p>
      These five self-authored statements are the foundation of your IPSAT 2.0 report.
      They capture how you described yourself in your own words, forming the qualitative
      layer from which your archetype was derived.
    </p>
    <div *ngFor="let s of aiContent.identity_statements" class="m-b-md">
      <h4 class="font-bold m-b-xs">{{ s.statement_type }}</h4>
      <p class="m-b-none">{{ s.statement_text }}</p>
    </div>

    <p class="font-italic text-muted m-t-lg">
      The IPSAT 2.0 Enhanced Report unites quantitative self-discovery with qualitative
      reflection to reveal how you create value in {{ archetypeReport.mode === 'faith' ? "God's mission" : 'your purpose' }}.
      It transforms insight into integration &mdash; helping you live, lead, and multiply
      from your unique design.
    </p>
  </section>

  <!-- ============================================================ -->
  <!-- End-of-report disclaimer (Steve 2026-06-04)                  -->
  <!-- ============================================================ -->
  <div class="report-disclaimer">
    This report is intended as a developmental tool. It is not a clinical or
    diagnostic instrument. Coaching with a certified IPSAT coach is encouraged
    to provide depth of understanding with application.
  </div>

  <!-- Footer -->
  <div class="report-footer hideNonPrint">
    <hr />
    <p class="text-center text-muted" style="font-size: 11px;">
      &copy; {{ (archetypeReport.generatedAt | date:'yyyy') }} nexleader &bull; IPSAT 2.0 &bull;
      Generated {{ getFormattedDate() }}
    </p>
  </div>

</div>
